import { useAuth } from '@/app/AuthContext.jsx';
import { useDocumentTitle } from '@/hooks';
import { extractList } from '@/lib/apiList';
import { qk } from '@/lib/queryKeys';
import { getPayments, getTask } from '@/lib/recepthor-api';
import { useToast } from '@/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import './PaymentStatus.css';

// Toku redirects here after payment. Confirm against the diligencia that was
// just paid — its taskId rides back in the success_url (?task=) — by reading
// the status of the task's newest PAYIN movement. A lookup not scoped to the
// task (e.g. the org's latest invoice) could read a DIFFERENT payment when
// several are in flight. Landing here can outrun Toku's webhook, so an
// unconfirmed payin (PENDING/PROCESSING) is "confirmando", never an error.
export default function PaymentStatus() {
  useDocumentTitle('Pago');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The paid diligencia, passed back by Toku in the success_url.
  const taskId = searchParams.get('task');
  const { organizationId } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const ran = useRef(false);

  useEffect(() => {
    // Wait for the active org (fresh boot after the Toku redirect); run once.
    if (ran.current || organizationId == null) return undefined;
    ran.current = true;
    let cancelled = false;
    (async () => {
      // The PAYIN movements of the paid diligencia, newest first. Explicit org:
      // the request must be pinned to the org the session resolved, not the
      // orgContext side-channel, which can be unset on a fresh boot.
      const payments = taskId
        ? await getPayments({
            taskId,
            type: 'PAYIN',
            organizationId,
            page: 1,
            pageSize: 20,
            sortKey: 'createdAt',
            sortBy: 'desc',
          }).catch(() => null)
        : null;
      if (cancelled) return;
      // The charge this return is about is the live attempt, and the database
      // allows at most one per quote. Asking for the non-failed row says that,
      // instead of trusting a position and reporting a superseded failure as
      // the outcome of the payment just made. Everything failed means the last
      // attempt failed.
      //
      // Note this row does NOT close on a declined card: one Toku invoice
      // carries several transactions, because the hosted checkout lets the
      // payer retry inside it, so the decline and the retry that follows are
      // two reports about this same row. Its status therefore cannot tell a
      // declined card from a charge still confirming, which is what the stage
      // below is read for.
      const movements = extractList(payments).filter((p) => p.type === 'PAYIN');
      const payin =
        movements.find((p) => p.status !== 'FAILED') ?? movements[0] ?? null;
      const payinStatus = payin?.status ?? null;
      // The diligencia's stage, which is where a decline is recorded. Read only
      // when the movement is unconfirmed, because a settled or closed charge
      // already answers the question and the extra request would buy nothing.
      //
      // `PAYMENT_REJECTED` is the conclusive case: the decline webhook has been
      // processed. Landing here can outrun that webhook, and then the stage is
      // still `PAYMENT_PROCESSING` and "confirmando" is the truthful answer, so
      // the ambiguity resolves the way it already did.
      const unconfirmed = payinStatus !== 'SUCCESS' && payinStatus !== 'FAILED';
      const task =
        taskId && unconfirmed
          ? await getTask(taskId, { organizationId }).catch(() => null)
          : null;
      if (cancelled) return;
      const declined = (task?.data ?? task)?.stage === 'PAYMENT_REJECTED';
      // El payin recién confirmado cambia payments/pagos/quotes/tasks:
      // refresco quieto de cada raíz.
      [
        qk.payments.all(),
        qk.pagos.all(),
        qk.quotes.all(),
        qk.activeDiligencias(),
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
      toast(
        payinStatus === 'SUCCESS'
          ? {
              kind: 'success',
              message:
                'Pago recibido: lo estamos confirmando con el receptor. La diligencia pasó a Procesando pago.',
              duration: 6000,
            }
          : payinStatus === 'FAILED' || declined
            ? {
                // The attempt closed, or the provider declined the card and the
                // diligencia already recorded it. Both leave the payer with the
                // same thing to do, and the charge is the same one either way:
                // the invoice takes the retry.
                kind: 'err',
                message:
                  'El pago no se completó. Puedes reintentar el pago desde la diligencia.',
                duration: 8000,
              }
            : {
                // PENDING / PROCESSING / movement not visible yet / lookup
                // error:
                // the webhook may simply not have arrived — confirming, not
                // an error.
                kind: 'warn',
                message:
                  'Estamos confirmando tu pago. El estado de la diligencia se actualizará en unos minutos.',
                duration: 8000,
              },
      );
      navigate(taskId ? `/diligencias/${taskId}` : '/diligencias', {
        replace: true,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, queryClient, taskId, organizationId, toast]);

  return (
    <main className="payst-main" aria-busy="true">
      <div className="payst-card">
        <span className="payst-spin" aria-hidden="true" />
        Confirmando el pago…
      </div>
    </main>
  );
}
